// src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Button, Typography,
  Table, Tag, Progress, Space, Avatar, Badge,
  Divider, Select, message, Alert, Tooltip
} from 'antd';
import {
  TeamOutlined, CarOutlined, DollarOutlined, FileTextOutlined,
  ArrowUpOutlined, ArrowDownOutlined, CalendarOutlined,
  CheckCircleOutlined, ClockCircleOutlined, EyeOutlined,
  SyncOutlined, ShoppingCartOutlined,
  ThunderboltOutlined, WifiOutlined, HistoryOutlined,
  UserOutlined, IdcardOutlined, TruckOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';

// Hooks et Context
import { useDashboard } from '../contexts/DashboardContext';
import useDashboardRefresh from '../hooks/useAutoRefresh';
import api from '../utils/axios';

dayjs.extend(relativeTime);
dayjs.locale('fr');

const { Title, Text } = Typography;
const { Option } = Select;

const DashboardPage = () => {
  const { refreshKey, lastUpdate = {}, pendingUpdates = [], refreshDashboard } = useDashboard();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState(null);
 
  // DONNÉES DYNAMIQUES - Statistiques calculées en temps réel
  const [stats, setStats] = useState({
    totalClients: 0,
    totalChauffeurs: 0,
    totalVentes: 0,
    totalAchats: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    activeMissions: 0,
    revenueChange: 0,
    clientChange: 0,
    chauffeurChange: 0,
    efficiency: 0,
    // Nouvelles statistiques dynamiques
    clientsAJour: 0,
    clientsEnAttente: 0,
    ventesValidees: 0,
    ventesPayees: 0,
    ventesEnAttente: 0,
    ventesAnnulees: 0,
    chauffeursLibres: 0,
    chauffeursEnMission: 0,
    chauffeursInactifs: 0,
    achatTotalTonnes: 0,
    achatTotalMontant: 0,
    achatPrixMoyen: 0,
    achatAvecErreurs: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentDateTime, setCurrentDateTime] = useState({ date: '', time: '', full: '' });

  // 🔥 ÉCOUTEUR DES ÉVÉNEMENTS DE MODIFICATION
  useEffect(() => {
    const handleDashboardRefresh = (event) => {
      console.log('🔄 Événement reçu:', event.detail);
      refreshDashboard(event.detail.resource, event.detail.action);
    };

    window.addEventListener('dashboard-refresh', handleDashboardRefresh);
    return () => window.removeEventListener('dashboard-refresh', handleDashboardRefresh);
  }, [refreshDashboard]);

  // Mettre à jour l'heure
  useEffect(() => {
    const updateDateTime = () => {
      const now = dayjs();
      setCurrentDateTime({
        date: now.format('dddd, D MMMM YYYY'),
        time: now.format('HH:mm:ss'),
        full: now.format('dddd, D MMMM YYYY [à] HH:mm:ss')
      });
    };
    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // 🔥 FONCTION PRINCIPALE DE CHARGEMENT DES DONNÉES - 100% DYNAMIQUE
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
   
    try {
      const dateRange = getDateRange(timeRange);
      const previousDateRange = getPreviousDateRange(timeRange);
     
      // CHARGEMENT DE TOUTES LES DONNÉES EN PARALLÈLE
      const [
        clientsRes,
        chauffeursRes,
        ventesRes,
        achatsRes,
        prevClientsRes,
        prevChauffeursRes,
        prevVentesRes
      ] = await Promise.allSettled([
        api.get('/api/clients', { params: dateRange }),
        api.get('/api/chauffeurs', { params: dateRange }),
        api.get('/api/ventes', { params: { ...dateRange, limit: 10000 } }),
        api.get('/api/achats', { params: { ...dateRange, limit: 10000 } }),
        api.get('/api/clients', { params: previousDateRange }),
        api.get('/api/chauffeurs', { params: previousDateRange }),
        api.get('/api/ventes', { params: { ...previousDateRange, limit: 10000 } })
      ]);

      // EXTRACTION SÉCURISÉE DES DONNÉES
      const extractData = (response) => {
        if (response.status === 'rejected') return [];
        const data = response.value.data;
        return data.data?.clients || data.data?.chauffeurs ||
               data.data?.ventes || data.data?.achats ||
               data.data || [];
      };

      const clients = extractData(clientsRes);
      const chauffeurs = extractData(chauffeursRes);
      const ventes = extractData(ventesRes);
      const achats = extractData(achatsRes);
      const prevClients = extractData(prevClientsRes);
      const prevChauffeurs = extractData(prevChauffeursRes);
      const prevVentes = extractData(prevVentesRes);

      // ============ STATISTIQUES CLIENTS ============
      const totalClients = clients.length;
      const clientsAJour = clients.filter(c => {
        const soldeRestant = parseFloat(c.soldeRestant) || 0;
        return soldeRestant <= 0 || c.statut === 'À jour';
      }).length;
      const clientsEnAttente = clients.filter(c => {
        const soldeRestant = parseFloat(c.soldeRestant) || 0;
        return soldeRestant > 0 || c.statut === 'En attente';
      }).length;

      // ============ STATISTIQUES CHAUFFEURS ============
      const totalChauffeurs = chauffeurs.length;
      const chauffeursLibres = chauffeurs.filter(c =>
        c.statut === 'Libre'
      ).length;
      const chauffeursEnMission = chauffeurs.filter(c =>
        c.statut === 'En Mission' || c.statut === 'En cours'
      ).length;
      const chauffeursInactifs = chauffeurs.filter(c =>
        c.statut === 'Inactif'
      ).length;

      // ============ STATISTIQUES VENTES ============
      const totalVentes = ventes.length;
      const ventesValidees = ventes.filter(v => v.statut === 'Validée').length;
      const ventesPayees = ventes.filter(v => v.statut === 'Payée').length;
      const ventesEnAttente = ventes.filter(v => v.statut === 'En attente').length;
      const ventesAnnulees = ventes.filter(v => v.statut === 'Annulée').length;
     
      const totalRevenue = ventes
        .filter(v => v.statut === 'Validée' || v.statut === 'Payée')
        .reduce((sum, v) => sum + (parseFloat(v.montant) || 0), 0);
     
      const prevRevenue = prevVentes
        .filter(v => v.statut === 'Validée' || v.statut === 'Payée')
        .reduce((sum, v) => sum + (parseFloat(v.montant) || 0), 0);

      // ============ STATISTIQUES ACHATS ============
      const totalAchats = achats.length;
     
      // Calcul des statistiques d'achats avec vérification des erreurs
      let achatTotalTonnes = 0;
      let achatTotalMontant = 0;
      let achatAvecErreurs = 0;
     
      achats.forEach(achat => {
        const quantite = parseFloat(achat.quantite) || 0;
        const prixParTonne = parseFloat(achat.prixParTonne) || 0;
        const total = parseFloat(achat.total) || 0;
        const calcule = Math.round(quantite * prixParTonne);
       
        achatTotalTonnes += quantite;
        achatTotalMontant += total;
       
        if (total !== calcule) {
          achatAvecErreurs++;
        }
      });
     
      const achatPrixMoyen = achatTotalTonnes > 0 ? achatTotalMontant / achatTotalTonnes : 0;

      // ============ CALCUL DES POURCENTAGES ============
      const revenueChange = prevRevenue === 0 ? 0 :
        Math.round(((totalRevenue - prevRevenue) / prevRevenue) * 100 * 10) / 10;
     
      const clientChange = prevClients.length === 0 ? 0 :
        Math.round(((totalClients - prevClients.length) / prevClients.length) * 100 * 10) / 10;
     
      const chauffeurChange = prevChauffeurs.length === 0 ? 0 :
        Math.round(((totalChauffeurs - prevChauffeurs.length) / prevChauffeurs.length) * 100 * 10) / 10;

      // ============ PERFORMANCE ============
      const efficiency = totalChauffeurs > 0 ?
        Math.round((chauffeursEnMission / totalChauffeurs) * 100) : 0;

      // ============ MISE À JOUR DU STATE ============
      setStats({
        // Stats générales
        totalClients,
        totalChauffeurs,
        totalVentes,
        totalAchats,
        totalRevenue,
        pendingOrders: ventesEnAttente,
        activeMissions: chauffeursEnMission,
        revenueChange,
        clientChange,
        chauffeurChange,
        efficiency,
        // Stats détaillées clients
        clientsAJour,
        clientsEnAttente,
        // Stats détaillées ventes
        ventesValidees,
        ventesPayees,
        ventesEnAttente,
        ventesAnnulees,
        // Stats détaillées chauffeurs
        chauffeursLibres,
        chauffeursEnMission,
        chauffeursInactifs,
        // Stats détaillées achats
        achatTotalTonnes,
        achatTotalMontant,
        achatPrixMoyen,
        achatAvecErreurs
      });

      // Génération des activités récentes (max 8)
      setRecentActivity(generateRecentActivity(clients, chauffeurs, ventes, achats));
     
      // Génération des événements à venir
      setUpcomingEvents(generateUpcomingEvents(achats, ventes));

    } catch (error) {
      console.error('❌ Erreur chargement dashboard:', error);
      setError('Impossible de charger les données du dashboard');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // 🔥 RAFRAÎCHISSEMENT AUTOMATIQUE
  useDashboardRefresh(fetchDashboardData, [timeRange]);

  // Chargement initial
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Rafraîchissement manuel
  const handleManualRefresh = () => {
    refreshDashboard('manuel', 'refresh');
    fetchDashboardData();
    message.success('Dashboard actualisé');
  };

  // Génération des activités récentes - 100% dynamique
  const generateRecentActivity = (clients, chauffeurs, ventes, achats) => {
    const activities = [];
   
    // Ajouter les clients récents (max 2)
    (clients || []).slice(-2).reverse().forEach(client => {
      if (client) {
        activities.push({
          id: client._id || client.id || `client-${Date.now()}-${Math.random()}`,
          type: 'client',
          action: 'Nouveau client',
          name: `${client.prenom || ''} ${client.nom || ''}`.trim() || 'Client',
          time: client.createdAt || client.dateCreation ? dayjs(client.createdAt || client.dateCreation).fromNow() : 'Récemment',
          status: 'success',
          priority: 'medium',
          link: '/clients'
        });
      }
    });

    // Ajouter les chauffeurs récents (max 2)
    (chauffeurs || []).slice(-2).reverse().forEach(chauffeur => {
      if (chauffeur) {
        activities.push({
          id: chauffeur._id || chauffeur.id || `chauffeur-${Date.now()}-${Math.random()}`,
          type: 'vehicle',
          action: chauffeur.statut === 'En Mission' ? 'Chauffeur en mission' : 'Nouveau chauffeur',
          name: `${chauffeur.prenom || ''} ${chauffeur.nom || ''}`.trim() || 'Chauffeur',
          time: chauffeur.createdAt || chauffeur.dateEnregistrement ? dayjs(chauffeur.createdAt || chauffeur.dateEnregistrement).fromNow() : 'Récemment',
          status: chauffeur.statut === 'En Mission' ? 'warning' : 'success',
          priority: chauffeur.statut === 'En Mission' ? 'high' : 'medium',
          link: '/chauffeur'
        });
      }
    });

    // Ajouter les ventes récentes (max 2)
    (ventes || []).slice(-2).reverse().forEach(vente => {
      if (vente) {
        activities.push({
          id: vente._id || vente.id || `vente-${Date.now()}-${Math.random()}`,
          type: 'order',
          action: vente.statut === 'En attente' ? 'Vente en attente' :
                  vente.statut === 'Validée' ? 'Vente validée' :
                  vente.statut === 'Payée' ? 'Vente payée' : 'Vente annulée',
          name: vente.numeroFacture || `Vente #${(vente._id || vente.id || '').slice(-4)}`,
          time: vente.createdAt || vente.date ? dayjs(vente.createdAt || vente.date).fromNow() : 'Récemment',
          status: vente.statut === 'En attente' ? 'warning' :
                  vente.statut === 'Validée' ? 'processing' :
                  vente.statut === 'Payée' ? 'success' : 'error',
          priority: vente.statut === 'En attente' ? 'high' : 'medium',
          link: '/vente'
        });
      }
    });

    // Ajouter les achats récents (max 2)
    (achats || []).slice(-2).reverse().forEach(achat => {
      if (achat) {
        const quantite = parseFloat(achat.quantite) || 0;
        const prix = parseFloat(achat.prixParTonne) || 0;
        const total = parseFloat(achat.total) || 0;
        const calcule = Math.round(quantite * prix);
        const hasError = total !== calcule;
       
        activities.push({
          id: achat._id || achat.id || `achat-${Date.now()}-${Math.random()}`,
          type: 'payment',
          action: hasError ? '⚠️ Achat incohérent' : 'Nouvel achat',
          name: achat.nomUsine || `Achat #${(achat._id || achat.id || '').slice(-4)}`,
          time: achat.createdAt || achat.date ? dayjs(achat.createdAt || achat.date).fromNow() : 'Récemment',
          status: hasError ? 'error' : 'success',
          priority: hasError ? 'high' : 'low',
          link: '/achat'
        });
      }
    });

    // Trier par date (plus récent en premier) et limiter à 8
    return activities
      .sort((a, b) => {
        const timeA = a.time.includes('Récemment') ? 0 : dayjs().diff(dayjs(a.time, 'YYYY-MM-DD'));
        const timeB = b.time.includes('Récemment') ? 0 : dayjs().diff(dayjs(b.time, 'YYYY-MM-DD'));
        return timeA - timeB;
      })
      .slice(0, 8);
  };

  // Génération des événements - 100% dynamique
  const generateUpcomingEvents = (achats, ventes) => {
    const events = [];
   
    // Événements d'achats
    (achats || []).slice(-3).forEach(achat => {
      if (achat && achat.date) {
        const eventDate = dayjs(achat.date);
        const isToday = eventDate.isSame(dayjs(), 'day');
        const isTomorrow = eventDate.isSame(dayjs().add(1, 'day'), 'day');
        const isThisWeek = eventDate.isAfter(dayjs().startOf('week')) &&
                          eventDate.isBefore(dayjs().endOf('week'));
       
        let dateDisplay = eventDate.format('DD/MM');
        if (isToday) dateDisplay = 'Aujourd\'hui';
        if (isTomorrow) dateDisplay = 'Demain';
       
        events.push({
          id: achat._id || achat.id || `event-achat-${Date.now()}-${Math.random()}`,
          title: `Achat: ${achat.nomUsine || 'Usine'}`,
          description: `${achat.quantite || 0} tonnes - ${formatCurrency(achat.total || 0)}`,
          date: dateDisplay,
          dateObj: eventDate,
          type: 'payment',
          status: isToday ? 'today' : isTomorrow ? 'tomorrow' : 'upcoming',
          link: '/achat'
        });
      }
    });

    // Événements de ventes en attente
    (ventes || [])
      .filter(v => v.statut === 'En attente' && v.date)
      .slice(0, 3)
      .forEach(vente => {
        if (vente) {
          events.push({
            id: vente._id || vente.id || `event-vente-${Date.now()}-${Math.random()}`,
            title: `Validation vente`,
            description: `N° ${vente.numeroFacture || 'Inconnu'} - ${formatCurrency(vente.montant || 0)}`,
            date: 'À valider',
            type: 'delivery',
            status: 'urgent',
            link: '/vente'
          });
        }
      });

    // Trier par date (plus urgent en premier)
    return events
      .sort((a, b) => {
        if (a.status === 'today') return -1;
        if (b.status === 'today') return 1;
        if (a.status === 'tomorrow') return -1;
        if (b.status === 'tomorrow') return 1;
        if (a.status === 'urgent') return -1;
        if (b.status === 'urgent') return 1;
        if (a.dateObj && b.dateObj) return a.dateObj - b.dateObj;
        return 0;
      })
      .slice(0, 5);
  };

  // Utilitaires de date
  const getDateRange = (range) => {
    const now = dayjs();
    switch (range) {
      case 'week': return {
        start: now.startOf('week').format('YYYY-MM-DD'),
        end: now.endOf('week').format('YYYY-MM-DD')
      };
      case 'month': return {
        start: now.startOf('month').format('YYYY-MM-DD'),
        end: now.endOf('month').format('YYYY-MM-DD')
      };
      case 'quarter':
        const quarterStart = now.startOf('quarter');
        return {
          start: quarterStart.format('YYYY-MM-DD'),
          end: quarterStart.endOf('quarter').format('YYYY-MM-DD')
        };
      case 'year': return {
        start: now.startOf('year').format('YYYY-MM-DD'),
        end: now.endOf('year').format('YYYY-MM-DD')
      };
      default: return {
        start: now.startOf('month').format('YYYY-MM-DD'),
        end: now.endOf('month').format('YYYY-MM-DD')
      };
    }
  };

  const getPreviousDateRange = (range) => {
    const now = dayjs();
    switch (range) {
      case 'week': return {
        start: now.subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
        end: now.subtract(1, 'week').endOf('week').format('YYYY-MM-DD')
      };
      case 'month': return {
        start: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        end: now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      };
      case 'quarter': return {
        start: now.subtract(3, 'month').startOf('quarter').format('YYYY-MM-DD'),
        end: now.subtract(3, 'month').endOf('quarter').format('YYYY-MM-DD')
      };
      case 'year': return {
        start: now.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
        end: now.subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
      };
      default: return {
        start: now.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        end: now.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      };
    }
  };

  // Formatage
  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).replace('XOF', '').trim() + ' FCFA';
  };

  const formatTonnes = (tonnes) => {
    return (tonnes || 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' t';
  };

  // Colonnes du tableau d'activités
  const activityColumns = [
    {
      title: 'Activité',
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => (
        <Space align="start">
          <Avatar
            size={32}
            className={
              record.type === 'client' ? 'bg-blue-100 text-blue-600' :
              record.type === 'vehicle' ? 'bg-yellow-100 text-yellow-600' :
              record.type === 'order' ? 'bg-green-100 text-green-600' :
              record.type === 'payment' ? 'bg-purple-100 text-purple-600' : 'bg-red-100 text-red-600'
            }
            icon={
              record.type === 'client' ? <UserOutlined /> :
              record.type === 'vehicle' ? <TruckOutlined /> :
              record.type === 'order' ? <FileTextOutlined /> :
              record.type === 'payment' ? <ShoppingCartOutlined /> : <ClockCircleOutlined />
            }
          />
          <div>
            <Text strong className="block">{text}</Text>
            <Text type="secondary" className="text-xs">{record.name}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = {
          success: { color: '#52c41a', text: 'Terminé' },
          processing: { color: '#1890ff', text: 'Validé' },
          warning: { color: '#faad14', text: 'En cours' },
          error: { color: '#ff4d4f', text: 'Attention' }
        };
        return (
          <Tag color={config[status]?.color || '#1890ff'}>
            {config[status]?.text || 'En attente'}
          </Tag>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'time',
      key: 'time',
      width: 100,
      render: (time) => (
        <Text type="secondary" className="text-xs">{time || 'Récemment'}</Text>
      )
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        record.link && (
          <Link to={record.link}>
            <Button type="link" size="small" icon={<EyeOutlined />} />
          </Link>
        )
      )
    }
  ];

  const eventColumns = [
    {
      title: 'Événement',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Tag color={
              record.status === 'today' ? 'red' :
              record.status === 'tomorrow' ? 'orange' :
              record.status === 'urgent' ? 'gold' : 'blue'
            }>
              {record.status === 'today' ? 'Aujourd\'hui' :
               record.status === 'tomorrow' ? 'Demain' :
               record.status === 'urgent' ? 'Urgent' : 'À venir'}
            </Tag>
            <Text strong>{text}</Text>
          </Space>
          {record.description && (
            <Text type="secondary" className="text-xs">{record.description}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date, record) => (
        <Space direction="vertical" align="end" size={0}>
          <Text strong>{date}</Text>
          {record.link && (
            <Link to={record.link}>
              <Button type="link" size="small" icon={<EyeOutlined />} />
            </Link>
          )}
        </Space>
      )
    },
  ];

  return (
    <div className="p-6">
      {/* Barre d'état du dashboard */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Badge
            status="success"
            text={
              <span className="flex items-center gap-1">
                <WifiOutlined /> Dashboard actif
              </span>
            }
          />
          {pendingUpdates && pendingUpdates.length > 0 && (
            <Tooltip title={`${pendingUpdates.length} mise(s) à jour récente(s)`}>
              <Badge
                count={pendingUpdates.length}
                style={{ backgroundColor: '#52c41a' }}
              >
                <HistoryOutlined className="text-lg" />
              </Badge>
            </Tooltip>
          )}
          {lastUpdate && lastUpdate.timestamp && (
            <Text type="secondary" className="text-sm">
              <SyncOutlined spin={loading} />
              Dernière maj: {dayjs(lastUpdate.timestamp).format('HH:mm:ss')}
              {lastUpdate.resource && (
                <Tag color="blue" className="ml-2">
                  {lastUpdate.resource}: {lastUpdate.action}
                </Tag>
              )}
            </Text>
          )}
        </div>
        <div className="flex gap-2">
          <Select
            value={timeRange}
            onChange={setTimeRange}
            size="middle"
            className="w-40"
          >
            <Option value="week">Cette semaine</Option>
            <Option value="month">Ce mois</Option>
            <Option value="quarter">Ce trimestre</Option>
            <Option value="year">Cette année</Option>
          </Select>
          <Button
            type="primary"
            icon={<SyncOutlined spin={loading} />}
            onClick={handleManualRefresh}
            loading={loading}
          >
            Actualiser
          </Button>
        </div>
      </div>

      {/* En-tête */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <Title level={2} className="mb-2">Tableau de Bord</Title>
          <div className="flex items-center gap-2 text-gray-500">
            <CalendarOutlined />
            <span>{currentDateTime.full}</span>
          </div>
        </div>
        <div className="bg-blue-50 px-6 py-3 rounded-lg">
          <div className="text-sm text-gray-600">Heure actuelle</div>
          <div className="text-2xl font-bold text-blue-600 font-mono">
            {currentDateTime.time}
          </div>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <Alert
          message="Erreur de chargement"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* ============ LIGNE 1: STATISTIQUES PRINCIPALES ============ */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <Text type="secondary">Total Clients</Text>
                <div className="text-2xl font-bold mt-2">{stats.totalClients}</div>
                <div className="flex items-center mt-2">
                  {stats.clientChange > 0 ? (
                    <ArrowUpOutlined className="text-green-500" />
                  ) : stats.clientChange < 0 ? (
                    <ArrowDownOutlined className="text-red-500" />
                  ) : null}
                  {stats.clientChange !== 0 && (
                    <Text className="ml-1 text-sm">
                      {stats.clientChange > 0 ? '+' : ''}{stats.clientChange}%
                    </Text>
                  )}
                  {stats.clientChange === 0 && (
                    <Text className="ml-1 text-sm text-gray-500">Stable</Text>
                  )}
                </div>
                <div className="mt-1 text-xs">
                  <span className="text-green-600">✓ {stats.clientsAJour}</span>
                  <span className="mx-1">·</span>
                  <span className="text-orange-600">⏳ {stats.clientsEnAttente}</span>
                </div>
              </div>
              <Avatar className="bg-blue-500" icon={<TeamOutlined />} />
            </div>
            <Divider className="my-4" />
            <Link to="/clients">
              <Button type="link" icon={<EyeOutlined />} className="p-0">
                Voir les clients
              </Button>
            </Link>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <Text type="secondary">Total Chauffeurs</Text>
                <div className="text-2xl font-bold mt-2">{stats.totalChauffeurs}</div>
                <div className="flex items-center mt-2">
                  {stats.chauffeurChange > 0 ? (
                    <ArrowUpOutlined className="text-green-500" />
                  ) : stats.chauffeurChange < 0 ? (
                    <ArrowDownOutlined className="text-red-500" />
                  ) : null}
                  {stats.chauffeurChange !== 0 && (
                    <Text className="ml-1 text-sm">
                      {stats.chauffeurChange > 0 ? '+' : ''}{stats.chauffeurChange}%
                    </Text>
                  )}
                  {stats.chauffeurChange === 0 && (
                    <Text className="ml-1 text-sm text-gray-500">Stable</Text>
                  )}
                </div>
                <div className="mt-1 text-xs">
                  <span className="text-green-600">✓ {stats.chauffeursLibres}</span>
                  <span className="mx-1">·</span>
                  <span className="text-orange-600">🚚 {stats.chauffeursEnMission}</span>
                  <span className="mx-1">·</span>
                  <span className="text-red-600">⏸️ {stats.chauffeursInactifs}</span>
                </div>
              </div>
              <Avatar className="bg-yellow-500" icon={<CarOutlined />} />
            </div>
            <Divider className="my-4" />
            <Link to="/chauffeur">
              <Button type="link" icon={<EyeOutlined />} className="p-0">
                Voir les chauffeurs
              </Button>
            </Link>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <Text type="secondary">Total Ventes</Text>
                <div className="text-2xl font-bold mt-2">{stats.totalVentes}</div>
                <div className="flex items-center mt-2">
                  <Text className="text-sm text-orange-500">
                    {stats.pendingOrders} en attente
                  </Text>
                </div>
                <div className="mt-1 text-xs">
                  <span className="text-green-600">✓ {stats.ventesValidees}</span>
                  <span className="mx-1">·</span>
                  <span className="text-blue-600">💰 {stats.ventesPayees}</span>
                  <span className="mx-1">·</span>
                  <span className="text-red-600">✗ {stats.ventesAnnulees}</span>
                </div>
              </div>
              <Avatar className="bg-green-500" icon={<FileTextOutlined />} />
            </div>
            <Divider className="my-4" />
            <Link to="/vente">
              <Button type="link" icon={<EyeOutlined />} className="p-0">
                Voir les ventes
              </Button>
            </Link>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card className="hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <Text type="secondary">Total Achats</Text>
                <div className="text-2xl font-bold mt-2">{stats.totalAchats}</div>
                <div className="flex items-center mt-2">
                  <Text className="text-sm text-gray-500">Cette période</Text>
                </div>
                <div className="mt-1 text-xs">
                  <span className="text-purple-600">⚖️ {formatTonnes(stats.achatTotalTonnes)}</span>
                  {stats.achatAvecErreurs > 0 && (
                    <>
                      <span className="mx-1">·</span>
                      <span className="text-red-600">⚠️ {stats.achatAvecErreurs}</span>
                    </>
                  )}
                </div>
              </div>
              <Avatar className="bg-purple-500" icon={<ShoppingCartOutlined />} />
            </div>
            <Divider className="my-4" />
            <Link to="/achat">
              <Button type="link" icon={<EyeOutlined />} className="p-0">
                Voir les achats
              </Button>
            </Link>
          </Card>
        </Col>
      </Row>

      {/* ============ LIGNE 2: REVENU ET PERFORMANCE ============ */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} lg={12}>
          <Card title={
            <Space>
              <DollarOutlined />
              <span>Chiffre d'affaires</span>
            </Space>
          }>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {formatCurrency(stats.totalRevenue)}
              </div>
              <Text type="secondary">
                {timeRange === 'week' ? 'Cette semaine' :
                 timeRange === 'month' ? 'Ce mois' :
                 timeRange === 'quarter' ? 'Ce trimestre' : 'Cette année'}
              </Text>
              <Progress
                percent={Math.min(100, stats.totalRevenue > 0 ?
                  (stats.totalRevenue / 100000000) * 100 : 0)}
                status={stats.totalRevenue > 0 ? "active" : "normal"}
                strokeColor="#10b981"
                className="mt-4"
                format={(percent) => `${(percent).toFixed(0)}% de l'objectif`}
              />
              <div className="flex justify-between mt-2">
                <Text type="secondary">Évolution</Text>
                <Text strong className={
                  stats.revenueChange > 0 ? 'text-green-600' :
                  stats.revenueChange < 0 ? 'text-red-600' : 'text-gray-600'
                }>
                  {stats.revenueChange > 0 ? '+' : ''}
                  {stats.revenueChange}%
                  {stats.revenueChange === 0 && ' (stable)'}
                </Text>
              </div>
              <div className="mt-4 text-sm text-gray-500">
                <DollarOutlined /> Ventes validées: {stats.ventesValidees} ·
                <DollarOutlined className="ml-2" /> Payées: {stats.ventesPayees}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={
            <Space>
              <SafetyCertificateOutlined />
              <span>Performance opérationnelle</span>
            </Space>
          }>
            <Row gutter={24}>
              <Col span={12}>
                <div className="text-center">
                  <Progress
                    type="circle"
                    percent={stats.efficiency}
                    size={100}
                    strokeColor={stats.efficiency > 75 ? '#52c41a' :
                               stats.efficiency > 50 ? '#faad14' : '#ff4d4f'}
                  />
                  <div className="mt-2 font-medium">
                    Taux d'activité
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {stats.chauffeursEnMission} / {stats.totalChauffeurs} chauffeurs
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <ThunderboltOutlined className="text-blue-500 mr-2" />
                    <span className="font-medium">{stats.chauffeursEnMission} chauffeurs en mission</span>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <DollarOutlined className="text-green-500 mr-2" />
                    <span className="font-medium">Moyenne: {stats.totalVentes > 0 ?
                      formatCurrency(stats.totalRevenue / stats.totalVentes) : '0 FCFA'}
                    </span>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <ShoppingCartOutlined className="text-purple-500 mr-2" />
                    <span className="font-medium">Prix achat: {formatCurrency(stats.achatPrixMoyen)}/t</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ============ LIGNE 3: ACTIVITÉS ET ÉVÉNEMENTS ============ */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <HistoryOutlined />
                <span>Activités récentes</span>
              </Space>
            }
            extra={<Link to="/activity">Voir tout</Link>}
          >
            <Table
              dataSource={recentActivity}
              columns={activityColumns}
              pagination={false}
              size="small"
              rowKey="id"
              loading={loading}
              locale={{ emptyText: 'Aucune activité récente' }}
              className="[&_.ant-table-row]:cursor-pointer"
              onRow={(record) => ({
                onClick: () => record.link && window.location.assign(record.link)
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <CalendarOutlined />
                <span>Événements & alertes</span>
              </Space>
            }
            extra={<Link to="/calendar">Calendrier</Link>}
          >
            <Table
              dataSource={upcomingEvents}
              columns={eventColumns}
              pagination={false}
              size="small"
              rowKey="id"
              loading={loading}
              locale={{ emptyText: 'Aucun événement à venir' }}
              className="[&_.ant-table-row]:cursor-pointer"
              onRow={(record) => ({
                onClick: () => record.link && window.location.assign(record.link)
              })}
            />
          </Card>
        </Col>
      </Row>

      {/* ============ LIGNE 4: RÉSUMÉ DÉTAILLÉ ============ */}
      <Row gutter={[16, 16]} className="mt-6">
        <Col xs={24}>
          <Card
            title={
              <Space>
                <IdcardOutlined />
                <span>Résumé des opérations</span>
              </Space>
            }
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <Text strong>Ventes</Text>
                    <Tag color="blue">Période actuelle</Tag>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total ventes:</span>
                      <span className="font-bold">{stats.totalVentes}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Validées:</span>
                      <span className="text-green-600">{stats.ventesValidees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payées:</span>
                      <span className="text-blue-600">{stats.ventesPayees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>En attente:</span>
                      <span className="text-orange-600">{stats.ventesEnAttente}</span>
                    </div>
                  </div>
                </div>
              </Col>
             
              <Col xs={24} sm={8}>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <Text strong>Achats</Text>
                    <Tag color="purple">Cette période</Tag>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total achats:</span>
                      <span className="font-bold">{stats.totalAchats}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quantité totale:</span>
                      <span className="text-purple-600">{formatTonnes(stats.achatTotalTonnes)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Montant total:</span>
                      <span className="text-purple-600">{formatCurrency(stats.achatTotalMontant)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Prix moyen:</span>
                      <span className="text-gray-600">{formatCurrency(stats.achatPrixMoyen)}</span>
                    </div>
                  </div>
                </div>
              </Col>
             
              <Col xs={24} sm={8}>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <Text strong>Performance</Text>
                    <Tag color="green">Global</Tag>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Chiffre d'affaires:</span>
                      <span className="text-green-600 font-bold">{formatCurrency(stats.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Évolution CA:</span>
                      <span className={stats.revenueChange > 0 ? 'text-green-600' :
                                     stats.revenueChange < 0 ? 'text-red-600' : ''}>
                        {stats.revenueChange > 0 ? '+' : ''}{stats.revenueChange}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Efficacité chauffeurs:</span>
                      <span className="font-medium">{stats.efficiency}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Valeur moyenne/vente:</span>
                      <span className="text-gray-600">
                        {stats.totalVentes > 0 ? formatCurrency(stats.totalRevenue / stats.totalVentes) : '0 FCFA'}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;